import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Image } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { getRelationshipString, getPeriodString, getActivityFrequencyString } from '../../../utils/Constants';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import { getSurveys } from '../../../api/surveyApi';
import './AdminRespondentDetail.css';
import { QuestionItem, SocialSkill, Survey } from '../../../types';
import { getSocialSkills } from '../../../api/socialSkillApi';
import { formatSurveyResponse } from '../../../utils/Utils';

interface Candidate {
  id: number;
  name: string;
  email: string;
  user: {
    id: number;
    name: string;
  };
}

interface Response {
  id: number;
  res1_relationship_private: string;
  res1_relationship_detail: number;
  res1_work_company: string;
  res1_work_details: string;
  res1_work_period_start_year: number;
  res1_work_period_start_season: number;
  res1_work_period_end_year: number | null;
  res1_work_period_end_season: number | null;
  res1_currently_working: boolean;
  res1_activity_frequency: number;
  res2_q1: string;
  res2_q2: string;
  res2_q3: string;
  res2_q4: string;
  res2_q5: string;
  res2_q6: string;
  res2_q7: string;
  res2_q8: string;
  res2_q9: string;
  res2_q10: string;
  res3_social_skill: number[];
  res4_q1: string;
  res4_q2: string;
  res4_q3: string;
  res4_q4: string;
  res4_q5: string;
  res4_q6: string;
  res4_q7: string;
  res4_q8: string;
  res4_q9: string;
  res4_q10: string;
  created_at: string;
  updated_at: string;
}

interface Respondent {
  id: number;
  name: string;
  email: string;
  relationship: number;
  created_at: string;
  candidate: Candidate;
  response?: Response;
}

const AdminRespondentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [respondent, setRespondent] = useState<Respondent | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dynamicQuestions, setDynamicQuestions] = useState<Array<QuestionItem>>([]);
  const [surveyQuestions, setSurveyQuestions] = useState<Array<Survey>>([]);
  const [socialSkillQuestions, setSocialSkillQuestions] = useState<Array<SocialSkill>>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchRespondentDetails();
  }, [id, navigate]);

  const fetchQuestionTemplate = async (questionTemplateId: number): Promise<void> => {
    try {
      const response = await axios.get(`/admin/question-templates/${questionTemplateId}`);
      const questions = response.data.questions || [];

      const sortedQuestions = questions.sort((a: QuestionItem, b: QuestionItem) => a.order - b.order);
      setDynamicQuestions(sortedQuestions);
    } catch (error: unknown) {
      console.error('Error fetching question template:', error);
    }
  };

  const fetchSurveys = async (): Promise<void> => {
    try {
      const surveyData = await getSurveys();
      if (surveyData && surveyData.length > 0) {
        const sortedQuestions = surveyData.sort((a: Survey, b: Survey) => a.order - b.order);
        setSurveyQuestions(sortedQuestions);
      }
    } catch (error: unknown) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchSocialSkills = async (): Promise<void> => {
    try {
      const socialSkillData = await getSocialSkills();
      if (socialSkillData && socialSkillData.length > 0) {
        const sortedQuestions = socialSkillData.sort((a: SocialSkill, b: SocialSkill) => a.order - b.order);
        setSocialSkillQuestions(sortedQuestions);
      }
    } catch (error: unknown) {
      console.error('Error fetching social skills:', error);
      setSocialSkillQuestions([]);
    }
  };

  const fetchRespondentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/respondents/${id}`);
      setRespondent(response.data);

      if (response.data.candidate?.question_template_id) {
        await fetchQuestionTemplate(response.data.candidate.question_template_id);
      }

      await fetchSurveys();
      await fetchSocialSkills();
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('回答者詳細の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!respondent) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <Alert variant="danger">回答者の詳細情報が見つかりません。</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>
        <AdminNavigation />

        {error && <Alert variant="danger">{error}</Alert>}

        <h2 className="candidate-detail-title">{respondent.name}</h2>
        <div className="candidate-detail-section mb-4">
          <Row>
            <Col className="client-detail-label" md={1}>候補者名</Col>
            <Col className="client-detail-value" md={2}>{respondent.candidate?.name || '-'}</Col>
            <Col className="client-detail-label" md={1}>クライアント名</Col>
            <Col className="client-detail-value" md={2}>{respondent.candidate?.user?.name || '-'}</Col>
            <Col className="client-detail-label" md={2}>メールアドレス</Col>
            <Col className="client-detail-value" md={4}>{respondent.email}</Col>
          </Row>
          <Row>
            <Col className="client-detail-label" md={1}>関係性</Col>
            <Col className="client-detail-value" md={2}>{getRelationshipString(Number(respondent.relationship))}</Col>
            <Col className="client-detail-label" md={1}>回答日</Col>
            <Col className="client-detail-value" md={2}>{respondent.response?.created_at ? new Date(respondent.response.created_at).toLocaleDateString('ja-JP') : '-'}</Col>
            <Col className="client-detail-label" md={2}>アンケート結果</Col>
            <Col className="client-detail-value" md={4}>####</Col>
          </Row>
        </div>

        {respondent.response ? (
          <div className="form-section">
            <div className="form-section-header">
              回答内容
            </div>
            <div className="form-section-body">
              <div className="confirmation-section relationship-section">
                <div className='d-flex mb-3'>
                  <Image src="/confirm-mark.svg" />
                  <div className="confirmation-title">{respondent.candidate?.name || '候補者'}さんとの関係性</div>
                </div>
                <div className="confirmation-item">
                  <div className="confirmation-label">所属：</div>
                  <div className="confirmation-value">{respondent.response.res1_work_company}</div>
                </div>
                <div className="confirmation-item">
                  <div className="confirmation-label">関係性：</div>
                  <div className="confirmation-value">
                    {respondent.candidate?.name || '候補者'}さんの{getRelationshipString(Number(respondent.response.res1_relationship_detail))}<br/>
                    （{respondent.response.res1_relationship_private === 'work' ? '仕事やプライベートを一緒にした時間の方が長い' : 'プライベートで一緒に過ごした時間の方が長い'}）
                  </div>
                </div>
                <div className="confirmation-item">
                  <div className="confirmation-label">活動内容：</div>
                  <div className="confirmation-value">{respondent.response.res1_work_details}</div>
                </div>
                <div className="confirmation-item">
                  <div className="confirmation-label">期間：</div>
                  <div className="confirmation-value">
                    {respondent.response.res1_work_period_start_year ?? ''}年 {getPeriodString(Number(respondent.response.res1_work_period_start_season))} ～ 
                    {respondent.response.res1_currently_working
                     ? '現在'
                     : ` ${respondent.response.res1_work_period_end_year ?? ''}年 ${getPeriodString(Number(respondent.response.res1_work_period_end_season))}`}
                  </div>
                </div>
                <div className="confirmation-item">
                  <div className="confirmation-label">頻度：</div>
                  <div className="confirmation-value">{getActivityFrequencyString(Number(respondent.response.res1_activity_frequency))}</div>
                </div>
              </div>

              <div className="confirmation-section questions-confirm">
                <div className='d-flex mb-3'>
                  <Image src="/confirm-mark.svg" />
                  <div className="confirmation-title">記述式質問に対する回答</div>
                </div>
                <div className="form-label">全{dynamicQuestions.length}問 <span className="question-note">（この回答は{respondent.candidate?.name || '候補者'}さんに伝わりません）</span></div>
                {dynamicQuestions.length > 0 ? (
                  dynamicQuestions.map((question: QuestionItem, questionIndex) => (
                    <div key={`question-${questionIndex + 1}`} className="confirmation-item">
                      <div className="confirmation-label">
                        Q{questionIndex + 1}. {question.text.replace(/{candidate_name}|候補者/g, respondent.candidate?.name || '候補者')}
                      </div>
                      <div className="confirmation-value">{String(respondent.response?.[`res2_q${questionIndex + 1}` as keyof Response] || '未回答')}</div>
                    </div>
                  ))
                ) : (
                  <div className='questionnaire-confirm'>
                    <div className="text-center">質問が設定されていません。</div>
                  </div>
                )}
              </div>

              <div className="confirmation-section social-skills-confirm">
                <div className='d-flex mb-3'>
                  <Image src="/confirm-mark.svg" />
                  <div className="confirmation-title">選択式質問に対する回答</div>
                </div>
                <div className="form-label">全{socialSkillQuestions.length}問 <span className="question-note">（この回答は{respondent.candidate?.name || '候補者'}さんに伝わりません）</span></div>
                <div className='questionnaire-confirm'>
                  {socialSkillQuestions.length > 0 ? (
                    (() => {
                      const getResponseText = (value: number) => {
                        switch (value) {
                          case 4: return '非常にそう思う';
                          case 3: return 'そう思う';
                          case 2: return 'そう思わない';
                          case 1: return '全くそう思わない';
                          default: return '未回答';
                        }
                      };

                      return socialSkillQuestions.map((question, questionIndex) => {
                        const responseValue = respondent.response?.res3_social_skill?.[questionIndex] || 0;
                        return (
                          <div key={`social-skill-${questionIndex + 1}`} className="confirmation-item">
                            <div className="confirmation-label">Q{questionIndex + 1}. {question.text}</div>
                            <div className="confirmation-value">{getResponseText(responseValue)}</div>
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div className="text-center">質問が設定されていません。</div>
                  )}
                </div>
              </div>

              <div className="confirmation-section mb-2">
                <div className='d-flex mb-3'>
                  <Image src="/confirm-mark.svg" />
                  <div className="confirmation-title">アンケートの回答</div>
                </div>
                <div className="form-label">全{surveyQuestions.length}問</div>
                <div className='questionnaire-confirm'>
                  {surveyQuestions.length > 0 ? (
                    surveyQuestions.map((question: Survey, questionIndex) => {
                      const fieldValue = respondent.response?.[`res4_q${questionIndex + 1}` as keyof Response];
                      const displayValue = formatSurveyResponse(fieldValue, question.input_type);
                      
                      return (
                        <div key={`survey-${questionIndex + 1}`} className="confirmation-item">
                          <div className="confirmation-label">Q{questionIndex + 1}. {question.text}</div>
                          <div className="confirmation-value">{displayValue}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center">アンケートが設定されていません。</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="form-container">
            <div className="form-header">
              回答内容
            </div>
            <div className="form-body">
              <p className="text-center">この回答者からの回答はまだ提出されていません。</p>
            </div>
          </div>
        )}

      </Container>
    </div>
  );
};

export default AdminRespondentDetail;
